import { apiFetch } from './api';

const apiUrl = import.meta.env.VITE_API_URL;

/* Sistema › Funcionalidades — a GB Code liga e desliga partes do produto por
   empresa. Contrato: TDS-Plataform/docs/requisicoes-http/funcionalidades.md.
   Desenho: docs/designsystem/propostas/admin/fase-6-tela.html (Direção A). */

export type EstadoFuncionalidade = 'ligada' | 'so_escolhidas' | 'desligada';

export interface ExcecaoFuncionalidade {
    empresaId: number;
    empresaNome: string | null;
    ligada: boolean;
    /** Falso quando o estado global é "desligada": guardada e sem efeito. */
    emVigor: boolean;
    criadoEm: string;
}

export interface Funcionalidade {
    chave: string;
    nome: string;
    descricao: string;
    grupo: string;
    estado: EstadoFuncionalidade;
    estadoFabrica: EstadoFuncionalidade;
    /** Falso = nunca mexida, vale o estado de fábrica. */
    alterado: boolean;
    alteradoPorNome: string | null;
    alteradoEm: string | null;
    empresasComAcesso: number;
    excecoes: ExcecaoFuncionalidade[];
}

export interface FuncionalidadesLista {
    totalEmpresasAtivas: number;
    funcionalidades: Funcionalidade[];
}

export interface MudancaFuncionalidade {
    id: number;
    chave: string;
    /** Nulo = mudança do estado global. */
    empresaId: number | null;
    empresaNome: string | null;
    /** Estado (global) ou `sem_excecao` / `excecao_ligada` / `excecao_desligada`. */
    antes: string | null;
    depois: string | null;
    usuarioNome: string | null;
    impersonatedBy: number | null;
    em: string;
}

interface RespostaApi<T> {
    sucesso: boolean;
    mensagem: string;
    dados: T;
}

/** Lê o corpo como texto antes do JSON: rota inexistente numa API antiga devolve 404 vazio. */
const chamar = async <T>(rota: string, init?: RequestInit): Promise<RespostaApi<T>> => {
    const res = await apiFetch(`${apiUrl}/api/sistema/funcionalidades${rota}`, {
        ...init,
        headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    });
    const texto = await res.text();
    if (!texto) {
        if (res.status === 404) throw new Error('A API no ar ainda não tem esta tela. Reinicie a API.');
        if (res.status === 403) throw new Error('Esta área é só da GB Code.');
        throw new Error(`Falha ao falar com a API (${res.status}).`);
    }
    const corpo: RespostaApi<T> = JSON.parse(texto);
    if (!res.ok && corpo.sucesso !== false) throw new Error(`Falha ao falar com a API (${res.status}).`);
    return corpo;
};

const exigir = async <T>(rota: string, init?: RequestInit): Promise<{ dados: T; mensagem: string }> => {
    const corpo = await chamar<T>(rota, init);
    if (!corpo.sucesso) throw new Error(corpo.mensagem);
    return { dados: corpo.dados, mensagem: corpo.mensagem };
};

export const FuncionalidadesSistemaService = {
    listar: async () => (await exigir<FuncionalidadesLista>('')).dados,

    mudarEstado: (chave: string, estado: EstadoFuncionalidade) =>
        exigir<null>(`/${encodeURIComponent(chave)}/estado`, { method: 'PUT', body: JSON.stringify({ estado }) }),

    definirExcecao: (chave: string, empresaId: number, ligada: boolean) =>
        exigir<null>(`/${encodeURIComponent(chave)}/empresas/${empresaId}`, { method: 'PUT', body: JSON.stringify({ ligada }) }),

    removerExcecao: (chave: string, empresaId: number) =>
        exigir<null>(`/${encodeURIComponent(chave)}/empresas/${empresaId}`, { method: 'DELETE' }),

    historico: async (chave?: string, limite = 100) => {
        const qs = new URLSearchParams({ limite: String(limite) });
        if (chave) qs.set('chave', chave);
        return (await exigir<MudancaFuncionalidade[]>(`/historico?${qs}`)).dados;
    },
};

/* ── A mesma resolução da API (FuncionalidadeService.Resolver), para a aba de
   Empresas responder "tem?" e "por quê?" sem uma rota nova. ── */
export type FonteAcesso = 'global' | 'excecao' | 'excecao_sem_efeito';

export function acessoDaEmpresa(f: Funcionalidade, empresaId: number): { tem: boolean; fonte: FonteAcesso } {
    const excecao = f.excecoes.find(e => e.empresaId === empresaId);
    if (f.estado === 'desligada') return { tem: false, fonte: excecao ? 'excecao_sem_efeito' : 'global' };
    if (excecao) return { tem: excecao.ligada, fonte: 'excecao' };
    return { tem: f.estado === 'ligada', fonte: 'global' };
}

export const ROTULO_ESTADO: Record<EstadoFuncionalidade, string> = {
    ligada: 'Ligada',
    so_escolhidas: 'Só escolhidas',
    desligada: 'Desligada',
};
